import { useCallback, useEffect, useState } from 'react';
import { FOLDER_MIME, listChildren } from '../drive/client';
import type { DriveRoot } from '../drive/source';
import { isAudioFile } from '../lib/format';
import { NmButton } from './nm';
import { AlertIcon, BackIcon, BookIcon } from './icons';

interface Crumb {
  id: string;
  name: string;
}

const MY_DRIVE: Crumb = { id: 'root', name: 'My Drive' };

/** Browse Drive and choose the folder your audiobooks live in. */
export function FolderPicker({
  onCancel,
  onChoose,
}: {
  onCancel: () => void;
  onChoose: (root: DriveRoot) => void;
}) {
  const [stack, setStack] = useState<Crumb[]>([MY_DRIVE]);
  const [folders, setFolders] = useState<Crumb[]>([]);
  const [audioCount, setAudioCount] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  const here = stack[stack.length - 1] ?? MY_DRIVE;

  const load = useCallback(async (folderId: string) => {
     setStatus('loading');
     setError(null);
     try {
        const children = await listChildren(folderId);
        setFolders(
           children
             .filter((f) => f.mimeType === FOLDER_MIME)
             .map((f) => ({ id: f.id, name: f.name })),
        );
        setAudioCount(
           children.filter((f) => f.mimeType !== FOLDER_MIME && isAudioFile(f.name, f.mimeType))
             .length,
        );
        setStatus('ready');
     } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setStatus('error');
     }
  }, []);

  useEffect(() => {
     void load(here.id);
  }, [here.id, load]);

  useEffect(() => {
     const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onCancel();
     };
     window.addEventListener('keydown', onKey);
     return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
     <div className="scrim" role="presentation" onClick={onCancel}>
        <div
          className="nm-raised nm-lg dialog dialog--wide"
          role="dialog"
          aria-modal="true"
          aria-labelledby="picker-title"
          onClick={(e) => e.stopPropagation()}
      >
          <div className="picker__head">
            <NmButton
              size="sm"
              onClick={() => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s))}
              disabled={stack.length === 1}
              aria-label="Up one folder"
            >
              <BackIcon size={17} />
            </NmButton>
            <h2 id="picker-title" className="dialog__title picker__title">
              {here.name}
            </h2>
          </div>

          <p className="picker__path">{stack.map((c) => c.name).join('   ›    ')}</p>

          <div className="nm-inset picker__list">
            {status === 'loading' && <p className="picker__empty">Loading…</p>}

            {status === 'error' && (
               <p className="picker__empty picker__empty--error">
                 <AlertIcon size={18} /> {error}
               </p>
            )}

            {status === 'ready' && folders.length === 0 && (
               <p className="picker__empty">
                 No subfolders here
                 {audioCount > 0 ? ` — but ${audioCount} audio file${audioCount === 1 ? '' : 's'}.` : '.'}
               </p>
            )}

            {status === 'ready' &&
              folders.map((folder) => (
                 <button
                   key={folder.id}
                   type="button"
                   className="nm-press picker__item"
                   onClick={() => setStack((s) => [...s, folder])}
                 >
                   <BookIcon size={17} />
                   <span className="picker__item-name">{folder.name}</span>
                 </button>
              ))}
          </div>

          {audioCount > 0 && (
             <p className="picker__hint">
               {audioCount} audio file{audioCount === 1 ? '' : 's'} directly in this folder — each
               becomes a single-file book.
             </p>
          )}

        <div className="dialog__actions">
           <NmButton onClick={onCancel}>Cancel</NmButton>
           <NmButton
             className="nmbtn--primary"
             onClick={() => onChoose({ id: here.id, name: here.name })}
           >
             Use “{here.name}”
           </NmButton>
        </div>
      </div>
    </div>
  );
}
